import { ArrowLeft, ArrowRight, CircleAlert, LoaderCircle, Ruler, Sprout } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "../../app/router";
import { PageMeta } from "../../components/PageMeta";
import { Notice } from "../../components/Notice";
import type { BalconyProfile, DesignItem, SavedDesign } from "../../data/model";
import { planTemplates } from "../../data/plans";
import { standardProfile } from "../../data/scenarios";
import { servicePackages } from "../../data/services";
import { styles } from "../../data/taxonomy";
import { generatePlan, noFitReasons, chooseTemplate } from "../../lib/compatibility";
import { clearSavedDesign, readSavedDesign, writeSavedDesign } from "../../lib/storage";
import { ConsultationForm, ConsultationSuccess } from "./ConsultationForm";
import { EstimateReview } from "./EstimateReview";
import { ProfileWizard } from "./ProfileWizard";
import { Workspace } from "./Workspace";

type Phase = "input" | "generating" | "no-fit" | "workspace" | "review" | "consult" | "success";

export function DesignerPage() {
  const [params, setParams] = useSearchParams();
  const savedAtStart = useRef(readSavedDesign());
  const styleParam = params.get("style");
  const packageParam = params.get("package");
  const initialProfile = {
    ...standardProfile,
    styleId: styles.some((style) => style.id === styleParam) ? styleParam! : standardProfile.styleId,
  };
  const [profile, setProfile] = useState<BalconyProfile>(initialProfile);
  const [phase, setPhase] = useState<Phase>("input");
  const [design, setDesign] = useState<DesignItem[]>([]);
  const [packageId, setPackageId] = useState(
    servicePackages.some((entry) => entry.id === packageParam) ? packageParam! : "balanced",
  );
  const [saved, setSaved] = useState(false);
  const [lastRemoved, setLastRemoved] = useState<DesignItem | null>(null);
  const [receiptCode, setReceiptCode] = useState("");
  const [loadingLine, setLoadingLine] = useState(0);
  const previousDesignRef = useRef<DesignItem[]>([]);

  const template = useMemo(() => chooseTemplate(profile), [profile]);

  const restore = () => {
    const stored = readSavedDesign();
    if (!stored) return;
    setProfile(stored.profile);
    setDesign(stored.design);
    setPackageId(stored.packageId);
    setReceiptCode(stored.receiptCode ?? "");
    setPhase(stored.phase === "submitted" ? "success" : stored.phase === "reviewed" ? "review" : "workspace");
    setSaved(true);
    setParams(stored.receiptCode ? { restored: "1", state: "success" } : { restored: "1" });
  };

  useEffect(() => {
    if (params.get("restore") === "1") restore();
    if (params.get("reset") === "1") {
      clearSavedDesign();
      setParams({});
    }
    // Query values are entry instructions and intentionally handled only once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (phase !== "generating") return;
    setLoadingLine(0);
    const lineTimer = window.setInterval(() => setLoadingLine((line) => Math.min(2, line + 1)), 280);
    const timer = window.setTimeout(() => {
      window.clearInterval(lineTimer);
      const reasons = noFitReasons(profile);
      if (reasons.length) {
        setPhase("no-fit");
        setParams({ state: "no-fit" });
        return;
      }
      setDesign(generatePlan(profile));
      setPackageId(packageParam && servicePackages.some((entry) => entry.id === packageParam) ? packageParam : template.packageId);
      setPhase("workspace");
      setSaved(false);
      setParams({ state: "plan" });
    }, 900);
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(lineTimer);
    };
  }, [packageParam, phase, profile, setParams, template.packageId]);

  const generate = () => {
    setPhase("generating");
    setParams({ state: "generating" });
  };

  const updateDesign = (next: DesignItem[]) => {
    const removed = design.find((oldItem) => !next.some((newItem) => newItem.instanceId === oldItem.instanceId));
    if (removed) setLastRemoved(removed);
    previousDesignRef.current = design;
    setDesign(next);
    setSaved(false);
  };

  const save = (nextPhase: SavedDesign["phase"] = "editing", code?: string) => {
    writeSavedDesign({
      version: 1,
      phase: nextPhase,
      profile,
      design,
      packageId,
      savedAt: "2026-07-30T09:30:00+07:00",
      receiptCode: code,
    });
    setSaved(true);
    window.dispatchEvent(new Event("reunang-storage"));
  };

  const undo = () => {
    if (!lastRemoved) return;
    if (design.some((item) => item.instanceId === lastRemoved.instanceId)) return;
    setDesign([...design, lastRemoved]);
    setLastRemoved(null);
    setSaved(false);
  };

  const reset = () => {
    if (!window.confirm("Xóa bản phác và bắt đầu lại? Thao tác này chỉ xóa dữ liệu demo trên trình duyệt.")) return;
    clearSavedDesign();
    setProfile(initialProfile);
    setDesign([]);
    setPackageId("balanced");
    setReceiptCode("");
    setLastRemoved(null);
    setSaved(false);
    setPhase("input");
    setParams({});
    window.dispatchEvent(new Event("reunang-storage"));
  };

  const submitSuccess = (code: string) => {
    setReceiptCode(code);
    setPhase("success");
    save("submitted", code);
    setParams({ state: "success" });
  };

  return (
    <section className={`designer-route phase-${phase}`}>
      <PageMeta
        title="Xưởng ban công | Rêu Nắng"
        description="Công cụ mô phỏng đề xuất, tùy chỉnh, dự toán và lịch chăm ban công xanh."
      />
      <div className="container designer-container">
        {phase === "input" && (
          <ProfileWizard
            profile={profile}
            onChange={setProfile}
            onGenerate={generate}
            hasSaved={Boolean(savedAtStart.current)}
            onRestore={restore}
          />
        )}

        {phase === "generating" && (
          <div className="generating-state" role="status" aria-live="polite">
            <div className="loading-orbit"><LoaderCircle aria-hidden="true" /><Sprout aria-hidden="true" /></div>
            <span className="eyebrow">Đang dựng bằng dữ liệu cố định</span>
            <h1>{["Đang đối chiếu hướng nắng…", "Đang giữ chỗ cho lối đi…", "Đang cộng từng hạng mục…"][loadingLine]}</h1>
            <div className="loading-lines" aria-hidden="true"><i className={loadingLine >= 0 ? "done" : ""} /><i className={loadingLine >= 1 ? "done" : ""} /><i className={loadingLine >= 2 ? "done" : ""} /></div>
            <p>Không có request mạng hoặc mô hình AI đang chạy.</p>
            <button className="text-button" type="button" onClick={() => setPhase("input")}>Hủy và quay lại</button>
          </div>
        )}

        {phase === "no-fit" && (
          <div className="no-fit-state">
            <CircleAlert aria-hidden="true" />
            <span className="eyebrow">Trạng thái không phù hợp</span>
            <h1>Khoảng này chưa đủ cho bộ quy tắc hiện tại.</h1>
            <p>Với <strong>{profile.widthCm} × {profile.depthCm} cm</strong>, công cụ không thể giữ vùng cây và lối thao tác tối thiểu.</p>
            <ul>{noFitReasons(profile).map((reason) => <li key={reason}><Ruler aria-hidden="true" /> {reason}</li>)}</ul>
            <Notice tone="info" title="Đây không phải kết luận rằng bạn không thể trồng cây.">
              <p>Vườn đứng, một chậu đơn hoặc giải pháp đóng riêng cần khảo sát ngoài phạm vi demo.</p>
            </Notice>
            <div className="no-fit-actions">
              <button className="button button-secondary" type="button" onClick={() => { setPhase("input"); setParams({}); }}><ArrowLeft aria-hidden="true" /> Sửa kích thước</button>
              <button className="button button-primary" type="button" onClick={() => { setProfile(standardProfile); window.setTimeout(generate, 0); }}>Thử mẫu 3,2 × 1,45 m <ArrowRight aria-hidden="true" /></button>
            </div>
          </div>
        )}

        {phase === "workspace" && (
          <Workspace
            profile={profile}
            design={design}
            template={template ?? planTemplates[0]}
            packageId={packageId}
            saved={saved}
            lastRemoved={lastRemoved}
            onDesignChange={updateDesign}
            onPackageChange={(id) => { setPackageId(id); setSaved(false); }}
            onSave={() => save("editing")}
            onUndo={undo}
            onReview={() => { setPhase("review"); save("reviewed"); setParams({ state: "estimate" }); }}
            onBack={() => { setPhase("input"); setParams({}); }}
            onReset={reset}
          />
        )}

        {phase === "review" && (
          <EstimateReview
            profile={profile}
            design={design}
            packageId={packageId}
            onBack={() => { setPhase("workspace"); setParams({ state: "plan" }); }}
            onConsult={() => { setPhase("consult"); setParams({ state: "consult" }); }}
          />
        )}

        {phase === "consult" && (
          <ConsultationForm
            profile={profile}
            design={design}
            packageId={packageId}
            onBack={() => { setPhase("review"); setParams({ state: "estimate" }); }}
            onSuccess={submitSuccess}
          />
        )}

        {phase === "success" && (
          <ConsultationSuccess
            code={receiptCode}
            profile={profile}
            design={design}
            packageId={packageId}
            onReview={() => { setPhase("review"); setParams({ state: "estimate" }); }}
            onNew={reset}
          />
        )}
      </div>
    </section>
  );
}
